import { useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { z } from 'zod';

export type DrawerKind = 'case' | 'staff' | 'task';
const KINDS: readonly DrawerKind[] = ['case', 'staff', 'task'];

/** ドロワーの種類の日本語名(戻るボタンの文言に使う)。 */
export const DRAWER_KIND_LABEL: Readonly<Record<DrawerKind, string>> = {
  case: '案件',
  staff: '社員',
  task: '作業',
};

// URL 由来の値は信用しない。英数字・_・- の短い ID だけを通す。
const IdSchema = z.string().regex(/^[\w-]{1,32}$/);

export type DrawerRef = { kind: DrawerKind; id: string };

/** history の state に入れる「どのドロワーから開いたか」。state も URL と同じく信用せず、読むときに検証する。 */
const LocationStateSchema = z.object({
  drawerFrom: z.object({ kind: z.enum(KINDS), id: IdSchema }),
});

const readId = (params: URLSearchParams, key: DrawerKind): string | null => {
  const parsed = IdSchema.safeParse(params.get(key));
  return parsed.success ? parsed.data : null;
};

type OpenOptions = {
  /** 別のドロワーの中から開くとき、その遷移元。あると、開いたドロワーに「戻る」ボタンが出る。 */
  from?: DrawerRef;
};

export const useDrawerParams = () => {
  const [params, setParams] = useSearchParams();
  const location = useLocation();

  const caseId = readId(params, 'case');
  const staffId = readId(params, 'staff');
  const taskId = readId(params, 'task');

  // DrawerHost と同じ優先順(案件 → 社員 → 作業)で、いま出ているドロワー
  const current: DrawerRef | null =
    caseId !== null
      ? { kind: 'case', id: caseId }
      : staffId !== null
        ? { kind: 'staff', id: staffId }
        : taskId !== null
          ? { kind: 'task', id: taskId }
          : null;

  const parsedState = LocationStateSchema.safeParse(location.state);
  const from = parsedState.success ? parsedState.data.drawerFrom : null;
  // 出ているドロワー自身への「戻る」は意味がないので無視する
  const origin: DrawerRef | null =
    from !== null && current !== null && !(from.kind === current.kind && from.id === current.id)
      ? from
      : null;

  const open = useCallback(
    (kind: DrawerKind, id: string, options: OpenOptions = {}) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          KINDS.forEach((key) => next.delete(key));
          next.set(kind, id);
          return next;
        },
        // 表の行など「ドロワーの外」から開くときは state を空にして、前の遷移元を引き継がない
        { state: options.from === undefined ? null : { drawerFrom: options.from } },
      );
    },
    [setParams],
  );

  /** 遷移元のドロワーを開き直す(履歴に積むので、ブラウザの戻るも自然に動く)。遷移元がなければ何もしない。 */
  const back = useCallback(() => {
    if (origin !== null) open(origin.kind, origin.id);
  }, [origin, open]);

  const close = useCallback(() => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        KINDS.forEach((key) => next.delete(key));
        return next;
      },
      { replace: true, state: null },
    );
  }, [setParams]);

  return { caseId, staffId, taskId, origin, open, back, close };
};
